import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evaluationsApi } from '../praiseApi';
import type { AwardNomineeListItem } from '../praiseTypes';

const ID_PREFIX = 'award-qualification';

type Choice = '' | 'qualified' | 'disqualified';

/**
 * REQUIREMENTS QUALIFICATION — tags one nominee Qualified or Disqualified, with remarks.
 *
 * **Nothing is preselected** on a nominee not yet tagged: the answer is the evaluator's, not a default
 * they tick past (`bool?` on the wire). A DISQUALIFICATION needs its reason. Only a qualified nominee
 * moves on to Criteria Evaluation; the tag can be changed until the deliberation is recorded.
 */
export function QualifyNomineeModal({
  nominee,
  employeeName,
  onClose,
  onSaved,
}: {
  nominee: AwardNomineeListItem;
  employeeName: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [choice, setChoice] = useState<Choice>(
    nominee.isQualified == null ? '' : nominee.isQualified ? 'qualified' : 'disqualified',
  );
  const [remarks, setRemarks] = useState(nominee.qualificationRemarks ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const reasonMissing = choice === 'disqualified' && remarks.trim() === '';
  const canSave = choice !== '' && !reasonMissing && confirmed && !saving;
  const saveDisabledReason = saving
    ? null
    : choice === ''
      ? 'Choose Qualified or Disqualified.'
      : reasonMissing
        ? 'Give the reason for disqualifying.'
        : !confirmed
          ? 'Tick the confirm box to submit.'
          : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await evaluationsApi.qualify(nominee.id, { isQualified: choice === 'qualified', remarks: remarks.trim() || null });
      toast.success(`${employeeName ?? 'The nominee'} was tagged ${choice === 'qualified' ? 'Qualified' : 'Disqualified'}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not tag this nominee.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX, fieldIds: { isQualified: `${ID_PREFIX}-is-qualified-qualified` } });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    setChoice('qualified');
    setRemarks('Every document requirement submitted and complete.');
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Requirements qualification — ${employeeName ?? 'Nominee'}`}
      size="lg"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <RadioGroup<Choice>
          id={`${ID_PREFIX}-is-qualified`}
          label="Result"
          required
          columns={1}
          value={choice}
          onChange={setChoice}
          options={[
            {
              value: 'qualified',
              label: 'Qualified',
              description: 'The nominee meets the requirements and moves on to Criteria Evaluation.',
            },
            {
              value: 'disqualified',
              label: 'Disqualified',
              description: 'The nominee does not meet the requirements. They are not scored, and the reason is recorded.',
            },
          ]}
          error={error?.fieldError('isQualified') ?? undefined}
          disabled={saving}
        />

        <Textarea
          id={`${ID_PREFIX}-remarks`}
          label="Remarks"
          required={choice === 'disqualified'}
          description="What the evaluator found. Required when disqualifying — it is the reason the nominee's file records."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={reasonMissing ? 'Give the reason for disqualifying.' : error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
